import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { E_SIGN_TEMPLATES, E_SIGN_CONTRACTS, LEADS } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import Can from '../../components/permissions/Can';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

const ContractList = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [leadOptions, setLeadOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyContract, setHistoryContract] = useState(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(E_SIGN_CONTRACTS.LIST, {
        params: {
          'relations[]': ['lead', 'eSignTemplate', 'author', 'latestESignContractLog', 'eSignContractLogs'],
          page: pagination.page,
          per_page: pagination.per_page,
          ...(search ? { search } : {}),
        },
      });
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      setContracts(Array.isArray(list) ? list : []);
      const pag = data?.data?.pagination ?? data?.pagination ?? {};
      if (pag.total !== undefined) setPagination((p) => ({ ...p, total: pag.total }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load contracts');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const fetchLeads = useCallback(async () => {
    setLeadLoading(true);
    try {
      const { data } = await api.get(LEADS.LIST, {
        params: { 'relations[]': ['source', 'assignees'], paginate: 1, per_page: 500 },
      });
      const list = extractList(data);
      setLeadOptions(
        (Array.isArray(list) ? list : []).map((l) => ({
          id: l.id,
          label: l.name ? `${l.name}${l.email ? ` (${l.email})` : ''}` : l.email || String(l.id),
        }))
      );
    } catch (err) {
      toast.error('Failed to load leads');
      setLeadOptions([]);
    } finally {
      setLeadLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const { data } = await api.get(E_SIGN_TEMPLATES.LIST);
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      const arr = Array.isArray(list) ? list : [];
      setTemplateOptions(
        arr.map((t) => ({
          id: t.id,
          label: t.name || t.title || String(t.id),
        }))
      );
    } catch (err) {
      toast.error('Failed to load templates');
      setTemplateOptions([]);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  const handleOpenCreate = () => {
    setSelectedLead(null);
    setSelectedTemplate(null);
    setCreateOpen(true);
    fetchLeads();
    fetchTemplates();
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setSelectedLead(null);
    setSelectedTemplate(null);
  };

  const handleCreateContract = async () => {
    if (!selectedLead?.id || !selectedTemplate?.id) {
      toast.warn('Please select a lead and a template');
      return;
    }
    setCreateSubmitting(true);
    try {
      await api.post(E_SIGN_CONTRACTS.CREATE, {
        lead_id: selectedLead.id,
        template_id: selectedTemplate.id,
      });
      toast.success('Contract created successfully');
      handleCloseCreate();
      fetchContracts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create contract');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedContract(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedContract(null);
  };

  const handleOpenHistory = () => {
    const contract = selectedContract;
    if (!contract?.id) return;
    setHistoryContract(contract);
    handleCloseMenu();
    setHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryContract(null);
  };

  const handleResend = async () => {
    if (!selectedContract?.id) return;
    const row = selectedContract;
    const logs = row.e_sign_contract_logs ?? (row.e_sign_contract_log ? [row.e_sign_contract_log] : []);
    const firstLog = Array.isArray(logs) && logs.length ? logs[0] : null;
    const signerApiId =
      firstLog?.payload?.signer?.id ??
      row.e_sign_contract_log?.signer_api_id ??
      row.e_sign_contract_log?.payload?.signer?.id ??
      row.signer_api_id ??
      row.signers?.[0]?.id ??
      row.signer?.id;
    if (!signerApiId) {
      toast.warn('Cannot resend: signer is not available for this contract.');
      handleCloseMenu();
      return;
    }
    setActionLoadingId(selectedContract.id);
    handleCloseMenu();
    try {
      await api.post(E_SIGN_CONTRACTS.RESEND(selectedContract.id, signerApiId));
      toast.success('Contract resent');
      fetchContracts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend contract');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };



  const handleDownload = async () => {  // renamed from handleDownload since it can open or download
    if (!selectedContract?.id) return;
    const row = selectedContract;
    const log = row.e_sign_contract_log ??
      (Array.isArray(row.e_sign_contract_logs) && row.e_sign_contract_logs.length
        ? row.e_sign_contract_logs[0]
        : null);

    const status = (log?.status ?? log?.payload?.contract?.status ?? '').toLowerCase().trim();
    const signPageUrl = log?.payload?.signer?.sign_page_url ?? row.sign_page_url;

    setActionLoadingId(row.id);
    handleCloseMenu();

    if (status === 'sent' && signPageUrl) {
      window.open(signPageUrl, '_blank', 'noopener,noreferrer');
      toast.info('Opened signing page in new tab');
      setActionLoadingId(null);
      return;
    }

    // ── For signed/completed ──────────────────────────────────────────────
    if (['signed', 'completed', 'complete'].includes(status)) {
      if (signPageUrl) {
        // Option A: Try to open it anyway (many providers show view/completed page)
        window.open(signPageUrl, '_blank', 'noopener,noreferrer');
        toast.info('Opened completed document view in new tab (download from there if available)');
      } else {
        toast.warning('No signing/view URL available for this completed contract');
      }

      // Option B: If your backend can proxy or generate a signed download URL → use it instead
      // Example (uncomment/adapt if you add this later):
      /*
      try {
        const res = await api.get(`/e-sign/contracts/${row.id}/signed-pdf`, { responseType: 'blob' });
        // ... create blob URL, <a> download ...
        toast.success('Signed PDF downloaded');
      } catch (err) {
        toast.error('Could not download signed PDF – contact support');
      }
      */
    } else {
      toast.info(`Status is "${status}" – action not available`);
    }

    setActionLoadingId(null);
  };


  const getChipColor = (status) => {
    if (!status) return 'default';

    const lower = status.toLowerCase();

    const colorMap = {
      completed: 'success',   // green
      sent: 'warning',      // blue/light blue
      signed: 'default',   // gray
      declined: 'error',     // red
      // add more statuses here later if needed
    };

    return colorMap[lower] || 'default';
  };

  const getActionText = (status) => {
  if (!status) return 'Contract Download';

  const lower = status.toLowerCase();

  if (lower === 'Sent')     return 'Open contract';
  if (lower === 'signed')   return 'Download contract';
  // You can add more:
  // if (lower === 'completed') return 'View completed contract';
  // if (lower === 'declined')  return 'Contract declined';

  return 'Contract Download'; // default/fallback
};

  const handleCopySignUrl = async () => {
    if (!selectedContract?.id) return;
    handleCloseMenu();
    try {
      const { data } = await api.get(E_SIGN_CONTRACTS.SIGN_URL(selectedContract.id));
      const url = data?.data?.sign_url ?? data?.sign_url ?? data?.url ?? '';
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Sign URL copied to clipboard');
      } else {
        toast.warn('No sign URL available');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to get sign URL');
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            E-Signature Contracts List ({contracts.length})
          </Typography>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search contracts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Client Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Dba Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contract Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contract Signed At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expire At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Signer Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 80, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : contracts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No contracts found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contracts.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.lead?.name ?? '—'}</TableCell>
                        <TableCell>{row.lead?.dba_name ?? '—'}</TableCell>

                        <TableCell>


                          <Chip
                            sx={{ textTransform: 'capitalize' }}
                            label={row.e_sign_contract_log?.status || 'undefined'}
                            color={getChipColor(row.e_sign_contract_log?.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(row.e_sign_contract_log?.signed_at ?? '-')}</TableCell>
                        <TableCell>{formatDate(row.e_sign_contract_log?.expires_at ?? '-')}</TableCell>
                        <TableCell>{row.lead?.email ?? '—'}</TableCell>
                        <TableCell>{row.created_by?.name ?? row.author?.name ?? row.created_by ?? '—'}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <MoreVertIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Create Contract Dialog - Lead + Template select with search */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create Contract</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            options={leadOptions}
            value={selectedLead}
            onChange={(_, v) => setSelectedLead(v)}
            loading={leadLoading}
            getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lead"
                placeholder="Search and select lead..."
                margin="normal"
                fullWidth
              />
            )}
          />
          <Autocomplete
            options={templateOptions}
            value={selectedTemplate}
            onChange={(_, v) => setSelectedTemplate(v)}
            loading={templateLoading}
            getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Template"
                placeholder="Search and select template..."
                margin="normal"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateContract}
            disabled={!selectedLead?.id || !selectedTemplate?.id || createSubmitting}
            startIcon={createSubmitting ? <CircularProgress size={20} /> : null}
          >
            {createSubmitting ? 'Creating...' : 'Create Contract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Status modal - maps e_sign_contract_logs from contract list response */}
      <Dialog open={historyModalOpen} onClose={handleCloseHistoryModal} maxWidth="md" fullWidth>
        <DialogTitle>History Status</DialogTitle>
        <DialogContent dividers>
          {historyContract && (() => {
            const logsRaw = historyContract.e_sign_contract_logs ?? historyContract.eSignContractLogs ?? [];
            const list = Array.isArray(logsRaw) ? logsRaw : [];
            if (list.length === 0) {
              return (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No history for this contract.
                </Typography>
              );
            }
            return (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Expires at</TableCell>
                      <TableCell>Contract</TableCell>
                      <TableCell>Signer</TableCell>
                      <TableCell>Sign page URL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((log, index) => {
                      const signer = log.payload?.signer ?? {};
                      const contractPayload = log.payload?.contract ?? {};
                      const signPageUrl = signer.sign_page_url ?? log.sign_page_url;
                      return (
                        <TableRow key={log.id ?? index}>
                          <TableCell>{log.status ?? '—'}</TableCell>
                          <TableCell>{formatDate(log.created_at)}</TableCell>
                          <TableCell>{formatDate(log.expires_at)}</TableCell>
                          <TableCell>
                            {contractPayload.title ?? '—'}
                            {contractPayload.status ? ` (${contractPayload.status})` : ''}
                          </TableCell>
                          <TableCell>
                            {signer.name ?? '—'}
                            {signer.email ? ` · ${signer.email}` : ''}
                            {signer.mobile ? ` · ${signer.mobile}` : ''}
                          </TableCell>
                          <TableCell>
                            {signPageUrl ? (
                              <Typography component="a" href={signPageUrl} target="_blank" rel="noopener noreferrer" variant="body2">Open</Typography>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Row actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permission="eSignContract-view">
          <MenuItem onClick={handleOpenHistory}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>History Status</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="eSignContract-resend">
          <MenuItem onClick={handleResend}>
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Resend Contract</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="eSignContract-view-pdf">
          <MenuItem onClick={handleDownload}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{getActionText(contracts?.e_sign_contract_log?.status)}</ListItemText>
          </MenuItem>
        </Can>
        {/* <MenuItem onClick={handleCopySignUrl}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy Sign URL</ListItemText>
        </MenuItem> */}
      </Menu>
    </PageLayout>
  );
};

export default ContractList;
